/**
 * Auswahlliste „Bezugsantrag" – dieselbe im Anlegeformular und in den Stammdaten eines Antrags.
 *
 * Die Liste kommt einmal vollständig (`permitsApi.getProcedureReferences`) und wird im Client
 * gruppiert: erst die Anträge dieses Projekts, dann die anderer Projekte. Die fremden stehen erst
 * nach „Anträge anderer Projekte anzeigen“ am Listenende in der Auswahl (progressive disclosure) – der
 * Normalfall ist ein Bezug im eigenen Projekt. Ist bereits ein fremder Antrag gewählt, stehen
 * sie von Anfang an da, sonst fehlte dem Feld die gewählte Option.
 */
import { PermitProcedureReference } from "../../../../types";

export const KEIN_BEZUG = "";

export const bezeichnungVon = (referenz: PermitProcedureReference): string =>
  referenz.bezeichnung ?? referenz.antragsart;

export const bezugsantragOptionen = (
  referenzen: PermitProcedureReference[],
  projectId: string | undefined,
  /** Der Antrag selbst – er kann nicht auf sich selbst verweisen */
  eigeneId: string | undefined,
  /** Hat der Nutzer „Anträge anderer Projekte anzeigen“ gewählt? */
  andereEingeblendet: boolean,
  gewaehlteId: string,
) => {
  const auswaehlbar = referenzen.filter((referenz) => referenz.id !== eigeneId);
  const eigene = auswaehlbar.filter(
    (referenz) => referenz.project_id === projectId,
  );
  const fremde = auswaehlbar
    .filter((referenz) => referenz.project_id !== projectId)
    .sort(
      (a, b) =>
        a.project_name.localeCompare(b.project_name, "de") ||
        bezeichnungVon(a).localeCompare(bezeichnungVon(b), "de"),
    );

  const andereSichtbar =
    andereEingeblendet ||
    fremde.some((referenz) => referenz.id === gewaehlteId);
  // Ohne fremde Anträge gäbe es nur eine Gruppe – eine einzelne Überschrift erklärt dann
  // nichts und macht das Menü nur höher.
  const gruppiert = andereSichtbar && fremde.length > 0;

  const optionen = [
    { value: KEIN_BEZUG, label: "– kein Bezugsantrag –" },
    ...eigene.map((referenz) => ({
      value: referenz.id,
      label: bezeichnungVon(referenz),
      groupLabel: gruppiert ? "Dieses Projekt" : undefined,
    })),
    ...(andereSichtbar ? fremde : []).map((referenz) => ({
      value: referenz.id,
      // In der Gruppe "Andere Projekte" stehen mehrere Projekte nebeneinander, hier trägt
      // die Option den Projektnamen selbst.
      label: `${referenz.project_name} · ${bezeichnungVon(referenz)}`,
      groupLabel: "Andere Projekte",
    })),
  ];
  return {
    optionen,
    /** Gibt es fremde Anträge, die noch nicht in der Liste stehen? */
    kannErweitern: !andereSichtbar && fremde.length > 0,
  };
};

export const ANDERE_PROJEKTE_ANZEIGEN = "Anträge anderer Projekte anzeigen";
